import React, {createContext, useContext, useEffect, useState} from 'react';

import fetcher from "../fetcher";
import {useFetchHook} from "../hooks/useFetchHook";
import useDidMountEffect from "../hooks/useDidMountEffect";
import {useDebounce} from "@uidotdev/usehooks";
import {useThemeStore} from "../stores/themeStore";
import {isSafeLogoUrl, probeLogoUrl} from "../utils/companyLogo";
import {AuthContext} from "./AuthContext";

export const UserContext = createContext(undefined)

export const UserContextProvider = ( { children } ) => {
    const [fetchProfileResponse, fetchProfile] = useFetchHook(fetcher.getProfile)
    const [updateProfileResponse, updateProfile] = useFetchHook(fetcher.updateProfile)

    const [profileState, setProfileState] = useState(null)
    const debouncedProfile = useDebounce(profileState, 400)

    // The company logo is probed ONCE here, keyed on the account's logo URL, and
    // shared with every consumer (Copy Quote, PDF preview/download/print,
    // worksheet PDFs) via useCompanyLogo — so the same URL is never probed
    // per-consumer. `companyLogoUrl` is the proven-loadable URL (or null → the
    // company-name fallback); `isCompanyLogoLoading` is true only while a probe for
    // a safe URL is in flight, so one-shot actions can wait for a final value.
    const logoUrl = profileState?.account_logo_url
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null)
    const [isCompanyLogoLoading, setIsCompanyLogoLoading] = useState(false)

    // The profile is the source of truth for the light/dark theme
    // (profileState.theme is "Light" | "Dark"). The global themeStore stays the
    // rendering vehicle every page reads via `data-theme`, so mirror the profile
    // value into it (normalized to the store's lowercase form) whenever it changes.
    const setTheme = useThemeStore((state) => state.setTheme)

    const setProfileStateWithUpdate = (newState) => {
        setProfileState({...profileState, ...newState, _updateInBackend: true })
    }

    const setProfileDealStateWithoutUpdate = (newState) => {
        setProfileState({...profileState, ...newState, _updateInBackend: false })
    }

    const isFeatureFlagEnabled = (feature_flag_name) => {
        return profileState?.feature_flags?.includes(feature_flag_name)
    }

    const updateProfileFunc = () => {
        const updateObj = structuredClone(profileState)
        delete updateObj._updateInBackend
        return updateProfile(updateObj)
    }

    const refreshData = () => {
        setProfileState(null)
        fetchProfile()
    }

    const isSuperUser = () => {
        return Boolean(profileState?.is_superuser)
    }

    // Whether this user is an admin of their own account — the CRM's rule for who
    // may see and change a teammate's records. Distinct from isSuperUser(), which
    // is TrueDesking-internal access. The backend enforces it either way; this
    // only decides what the UI offers, so a missing or stale profile reads as the
    // lesser privilege.
    const isAccountAdmin = () => {
        return Boolean(profileState?.is_account_admin)
    }

    // Whether this user's role lets them price deals. Note the default is the
    // OPPOSITE of isAccountAdmin's: only a profile that positively says no
    // restricts anything, an absent key reads as allowed.
    //
    // Because `can_desk` is newer than this client. A payload without it is an
    // older backend, and failing closed there would take desking away from
    // every user of a deploy that simply hasn't shipped the field yet — a far
    // worse failure than briefly offering a button the backend refuses.
    // Restricting is the UI being tidy; the backend is the actual guard.
    const canDesk = () => {
        return profileState?.can_desk !== false
    }

    // useContext, not useAuthContext(): that hook throws without a provider, and
    // several suites mount UserContextProvider on its own. No AuthContext just
    // means there's no boot response to reuse.
    const bootProfile = useContext(AuthContext)?.bootProfile

    useEffect(function fetchProfileFromBackend() {
        // AuthContext already fetched the profile to validate the token on boot,
        // and we only mount once that resolved — reuse it rather than asking for
        // the same body again. An in-session login leaves none, so we still fetch.
        if (bootProfile) {
            setProfileDealStateWithoutUpdate(bootProfile)
            return
        }

        fetchProfile()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(function setFetchedProfile(){
        if (fetchProfileResponse?.data?.data) {
            setProfileDealStateWithoutUpdate(fetchProfileResponse?.data?.data)
        }
    }, [fetchProfileResponse])

    useEffect(function syncThemeFromProfile(){
        const t = profileState?.theme
        if (t === 'Light') setTheme('light')
        else if (t === 'Dark') setTheme('dark')
    }, [profileState?.theme])

    // Single company-logo probe, re-run only when account_logo_url changes (upload,
    // replace, remove, login, account switch). No configured / invalid URL settles
    // to the fallback immediately without probing; isStale drops a superseded probe
    // so a result for an old URL can't overwrite a newer logo. Never blocks the
    // subtree — the provider still renders while a probe is pending.
    useEffect(function probeCompanyLogo(){
        let isStale = false

        setCompanyLogoUrl(null)

        if (!isSafeLogoUrl(logoUrl)) {
            setIsCompanyLogoLoading(false)
            return
        }

        setIsCompanyLogoLoading(true)
        probeLogoUrl(logoUrl).then((result) => {
            if (!isStale) {
                setCompanyLogoUrl(result)
                setIsCompanyLogoLoading(false)
            }
        })

        return () => {
            isStale = true
        }
    }, [logoUrl])

    useDidMountEffect(function updateProfileInBackend(){
        if (profileState?._updateInBackend === true) {
            updateProfileFunc()
        }
    }, [debouncedProfile])

    useDidMountEffect(function updateProfileFromBackend(){
        if (updateProfileResponse?.data?.data && !updateProfileResponse.loading) {
            const updateObj = structuredClone(updateProfileResponse?.data?.data)
            delete updateObj._updateInBackend
            setProfileDealStateWithoutUpdate(updateObj)
        }
    }, [updateProfileResponse])

    useEffect(() => {
        return () => {
            setProfileState(null)
        }
    }, [])

    const value = {
        refreshData,
        profileState,
        setProfileState,

        updateProfile,
        updateProfileResponse,

        setProfileStateWithUpdate,
        setProfileDealStateWithoutUpdate,

        isSuperUser,
        isAccountAdmin,
        canDesk,
        isFeatureFlagEnabled,

        companyLogoUrl,
        isCompanyLogoLoading,
    }

    if (!profileState) return null;

    return (
        <UserContext.Provider value={value}>
            { children }
        </UserContext.Provider>
    )
}

export const useUserContext = () => {
    const context = useContext(UserContext)

    if (context === undefined) {
        throw new Error('useUserContext must be used within UserContext')
    }
    return context
}